import { OpponentLogo } from "@/components/dataDisplay"
import { Href } from "@/components/navigation"
import { Icon, type IconName } from "@/components/ui"
import OutcomeButton from "@/compositions/OutcomeButton/OutcomeButton"
import TimeValue from "@/compositions/TimeValue"
import { getGameDateTime } from "@/helpers/getters"
import { useActiveMarket, useActiveMarkets } from "@azuro-org/sdk"
import {
  ConditionState,
  type GameMarkets,
  type GamesQuery,
} from "@azuro-org/toolkit"
import React from "react"

type CardProps = {
  game: GamesQuery["games"][0]
}

type ConditionProps = {
  markets: GameMarkets
  game: GamesQuery["games"][0]
  section?: string
}

const AzuroTopEventCard: React.FC<CardProps> = ({ game }) => {
  const {
    sport: { slug: sportSlug },
    league: { name: leagueName, slug: leagueSlug },
    country: { name: countryName, slug: countrySlug },
    gameId,
    participants,
    startsAt,
    title,
  } = game

  const { date, time } = getGameDateTime(+startsAt * 1000)

  const { data: markets, isFetching } = useActiveMarkets({
    gameId: game.gameId,
  })

  return (
    <div className="bg-card-border-bottom p-px rounded-md overflow-hidden">
      <div className="bg-gradient-event-card p-4 rounded-md">
        <Href
          to={`${sportSlug}/${countrySlug}/${leagueSlug}/${gameId}`}
          className="flex items-center justify-center text-accent-grey-200 text-caption-13 hover:underline"
        >
          <Icon
            className="size-4 mr-2 flex-none"
            name={`sport/${sportSlug}` as IconName}
          />
          <span className="text-ellipsis whitespace-nowrap overflow-hidden">
            {countryName}
          </span>
          <div className="size-[2px] rounded-full bg-accent-grey-700 mx-1" />
          <span className="text-ellipsis whitespace-nowrap overflow-hidden">
            {leagueName}
          </span>
        </Href>
        <div className="mt-3 flex items-center justify-between px-7">
          <OpponentLogo image={participants[0].image} size={48} />
          <div className="text-caption-12 text-center">
            <div className="text-accent-grey-200">{date}</div>
            <div className="font-semibold mt-[2px]">
              <TimeValue time={time} />
            </div>
          </div>
          <OpponentLogo image={participants[1].image} size={48} />
        </div>
        <div className="mt-5 text-caption-13 font-semibold text-center text-ellipsis whitespace-nowrap overflow-hidden">
          {title}
        </div>
        <div className="mt-3 flex items-center space-x-2">
          {isFetching ? (
            <>
              <div className="bone w-full h-7 rounded-sm" />
              <div className="bone w-full h-7 rounded-sm" />
              <div className="bone w-full h-7 rounded-sm" />
            </>
          ) : (
            <Condition markets={markets!} game={game} section="Hot" />
          )}
        </div>
      </div>
    </div>
  )
}

const Condition: React.FC<ConditionProps> = ({ markets, game }) => {
  const { data } = useActiveMarket({ markets })

  const { marketsByKey, activeMarketKey, states } = data

  const { name, conditions } = marketsByKey[activeMarketKey!]
  const { conditionId, outcomes } = conditions[0]

  return (
    <>
      {outcomes.map(outcome => (
        <OutcomeButton
          key={outcome.outcomeId}
          marketName={name}
          outcome={outcome}
          game={game}
          isLocked={states[conditionId] !== ConditionState.Active}
        />
      ))}
    </>
  )
}

export default AzuroTopEventCard
